import { SourceGameType } from "domains/accountsBuffPlay/constants/source-options"

export const GameAttributionPlatform = {
  ADJUST: "ADJUST",
  APPS_FLYER: "APPS_FLYER",
  SINGULAR: "SINGULAR",
  EVERFLOW: "EVERFLOW",
} as const

export type GameAttributionPlatform = keyof typeof GameAttributionPlatform;

export const GameTaskType = {
  LEVEL_TASK: "LEVEL_TASK",
  BONUS_TASK: "BONUS_TASK",
  CASHBACK_TASK: "CASHBACK_TASK",
  INSTALL_TASK: "INSTALL_TASK",
  WATCH_AN_ADS_TASK: "WATCH_AN_ADS_TASK",
} as const

export type GameTaskType = keyof typeof GameTaskType;

export const GamePlatformType = { ANDROID: "ANDROID", IOS: "IOS" } as const

export type GamePlatformType = (typeof GamePlatformType)[keyof typeof GamePlatformType];

export type NativeGameFilter = {
  name?: string;
  gameSource?: SourceGameType;
  hideDeleted?: boolean;
};

export interface NativeGame {
  id: string
  name: string
  category: string
  bannerUrl: string
  description?: string | null
  earningSpeed: number
  isOnlyForEngagingAccount: boolean
  isTopPick: boolean
  videoBannerUrl: string | null
  isDeleted?: Date | null
  platforms: GamePlatform[]
  tasks: GameTask[]
}

export interface GameTask {
  id: string
  name: string
  platform: GamePlatformType
  rewardUsd: number | null
  sequence: number
  type: GameTaskType
  completed?: boolean
  appsflyerRevenue: number | null
  cpeRevenue: number | null
  bonusRewardUsd: number | null
  expiresInMinutes: number | null
  iconNumber: number
  taskToken: string
  adsWatchedTotalRequired?: number | null
}

export interface GamePlatform {
  platform: GamePlatformType
  platformId: string
  iconUrl: string
  rating: number
  installRevenue?: number
  appsflyerRevenue?: number | null
  adjustLinkToken?: string
  ctaLink: string
  ctaCampaign: string
  attributionPlatform: GameAttributionPlatform
  gamePublisher?: GamePublisher
  attributionWindowDays?: number | null
  isInstallable: boolean
  dailyBudget?: number
  monthlyBudget?: number
  isBudgetActive?: boolean
  dailyLimitReachedAt?: Date | null
  monthlyLimitReachedAt?: Date | null
}

export interface GamePublisher {
  id: string
  name: string
  costReportingPlatform: GameAttributionPlatform
}

export interface CreateNativeGameRequest {
  name: string
  category: string
  bannerUrl: string
  videoBannerUrl?: string | null
  earningSpeed?: number
  isTopPick?: boolean
  isOnlyForEngagingAccount?: boolean
  description?: string | null
  platforms: CreateGamePlatform[]
  tasks?: CreateGameTask[]
}

export interface CreateGamePlatform {
  platform: GamePlatformType
  platformId: string
  iconUrl: string
  rating: number
  installRevenue?: number
  appsflyerRevenue?: number | null
  adjustLinkToken?: string
  ctaLink: string
  ctaCampaign: string
  attributionPlatform: GameAttributionPlatform
  gamePublisherId: string
  attributionWindowDays?: number | null
  isInstallable?: boolean
  dailyBudget?: number
  monthlyBudget?: number
  isBudgetActive?: boolean
}

export type UpdateGamePlatform = Partial<CreateGamePlatform> & { platform: GamePlatformType }

export interface CreateGameTask {
  name: string
  platform: GamePlatformType

  /** Reward in USD. The API computes reward (points) = rewardUsd * 1000. Required end-to-end. */
  rewardUsd: number
  sequence: number
  type: GameTaskType
  appsflyerRevenue?: number | null
  cpeRevenue?: number | null

  /** Bonus reward in USD. The API computes bonusReward (points) = bonusRewardUsd * 1000. */
  bonusRewardUsd?: number | null
  expiresInMinutes?: number | null
  iconNumber?: number
  taskToken: string
  adsWatchedTotalRequired?: number | null
}

export type GamePublisherOption = Pick<GamePublisher, "id" | "name">;

export type UpdateGameTask = {
  gameId: string
  taskId: string
} & Partial<CreateGameTask>

export const BONUS_SPEND_TIER_COUNT = 4

export interface BonusSpendMission {
  id: string
  gameId: string
  platform: GamePlatformType
  level: number
  spendThresholdUsd: number
  rewardUsd: number
  enabled: boolean
}

export interface BonusSpendMissionTier {
  level: number
  spendThresholdUsd: number
  rewardUsd: number
  enabled: boolean
}

export const DailyMissionKind = {
  WATCH_AD: "WATCH_AD",
  DAILY_TASK: "DAILY_TASK",
  LOGIN_STREAK: "LOGIN_STREAK",
} as const

export type DailyMissionKind = keyof typeof DailyMissionKind;

export const DAILY_MISSION_KIND_OPTIONS: { value: DailyMissionKind; label: string }[] = [
  { value: DailyMissionKind.WATCH_AD, label: "Watch Ad" },
  { value: DailyMissionKind.DAILY_TASK, label: "Daily Task" },
  { value: DailyMissionKind.LOGIN_STREAK, label: "Login Streak" },
]

export interface DailyMission {
  id: string
  gameId: string
  platform: GamePlatformType
  kind: DailyMissionKind
  taskToken: string
  title: string
  descriptionTmpl: string
  rewardUsd: number
  requiredCount: number
  windowDays: number | null
  enabled: boolean
}

export interface CreateDailyMissionRequest {
  gameId: string
  platform: GamePlatformType
  kind: DailyMissionKind
  taskToken: string
  title: string
  descriptionTmpl: string
  rewardUsd: number
  requiredCount: number
  windowDays?: number | null
  enabled: boolean
}

export type UpdateDailyMissionRequest = Partial<Omit<CreateDailyMissionRequest, "gameId" | "platform">>;
